/**
 * Referrals API — data layer for the referral card type (tables referrers,
 * referrals; migration 28). Sits beside filesApi.js and prepRequestsApi.js.
 *
 * A referral is a card for a prospect someone sent to the practice. It is a
 * SEPARATE record type from files, preparation requests and review planner
 * rows (see src/utils/referrals.js for the vocabulary and the transition
 * rules). This module:
 *
 *   - never writes to files, preparation_requests, review_clients or any
 *     notes table other than referral_notes;
 *   - never imports from dashboardStats / bonusCalculator / bonusClock /
 *     slaCalculator / prepRequests / reviewPlanner (stats separation,
 *     CLAUDE.md §21.1);
 *   - stores NO contact details for the prospect or the referrer (POPIA
 *     minimality) — there is no column for them and no field is mapped;
 *   - honours the is_test pattern (§33): the flag is set only at creation,
 *     mapped back as `isTest`, and every list returns test rows too (they
 *     always render on the board). Aggregates are computed elsewhere, in the
 *     referral stats utility, which filters `isTest !== true` at source.
 *
 * Every status transition writes the matching system note to the referral's
 * notepad (the §7.5 / §18.3 house pattern), swallowing note failures so a
 * notes outage can never block a transition.
 */

import supabase from './supabaseClient';
import { addBusinessDays } from '../utils/businessDays';
import { createNote, createSystemNote, NOTE_BODY_MAX } from './referralNotesApi';
import {
  REFERRAL_CHANNELS,
  REFERRAL_CHANNEL_LABELS,
  REFERRAL_OUTCOMES,
  REFERRAL_OUTCOME_LABELS,
  REFERRAL_STATUS_LABELS,
  REFERRER_TYPES,
  REFERRAL_NEXT_ACTION_BUSINESS_DAYS,
  canTransitionReferral,
} from '../utils/referrals';

// ── Row mapping ─────────────────────────────────────────────────────────────

function mapRowToReferrer(row) {
  if (!row) return null;
  return {
    id: row.id,
    displayName: row.display_name || '',
    referrerType: row.referrer_type || 'client',
    hasReferralArrangement: row.has_referral_arrangement === true,
    createdAt: row.created_at || null,
    isTest: row.is_test === true,
  };
}

function mapRowToReferral(row) {
  return {
    id: row.id,
    referrerId: row.referrer_id,
    // Embedded referrer (present when the query selected `referrers(*)`).
    referrer: mapRowToReferrer(row.referrers),
    prospectName: row.prospect_name || '',
    channel: row.channel || null,
    reLine: row.re_line || null,
    receivedOn: row.received_on || null, // 'YYYY-MM-DD'
    pipelineStatus: row.pipeline_status || 'inbox',
    progressContactedAt: row.progress_contacted_at || null,
    progressMeetingAt: row.progress_meeting_at || null,
    nextActionOn: row.next_action_on || null, // 'YYYY-MM-DD'
    pauseReason: row.pause_reason || null,
    outcome: row.outcome || null,
    linkedFileId: row.linked_file_id || null,
    feedbackGivenAt: row.feedback_given_at || null,
    completedAt: row.completed_at || null,
    // Voice note recorded on the capture form (signed storage URL) and its
    // AI summary. Kept on the row, not in the notepad, because note bodies
    // are capped at 200 characters.
    voiceNoteUrl: row.voice_note_url || null,
    voiceSummary: row.voice_summary || null,
    createdBy: row.created_by || '', // email (text), mirrors files.submitted_by
    createdAt: row.created_at || null,
    updatedAt: row.updated_at || null,
    isTest: row.is_test === true,
  };
}

// ── Small helpers ───────────────────────────────────────────────────────────

/** 'YYYY-MM-DD' from a Date pinned to 12:00 UTC (addBusinessDays output). */
function toYmdUTC(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  const d = String(date.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

const YMD_RE = /^\d{4}-\d{2}-\d{2}$/;

function assertYmd(value, label) {
  if (typeof value !== 'string' || !YMD_RE.test(value)) {
    throw new Error(`${label} must be a YYYY-MM-DD date.`);
  }
  return value;
}

/** Display a 'YYYY-MM-DD' as DD/MM/YYYY for note bodies (house style). */
function ymdToDisplay(ymd) {
  if (typeof ymd !== 'string' || !YMD_RE.test(ymd)) return '';
  const [y, m, d] = ymd.split('-');
  return `${d}/${m}/${y}`;
}

/** Escape the PostgREST pattern characters for an ilike match. */
function escapeLike(value) {
  return String(value).replace(/[\\%_]/g, (ch) => `\\${ch}`);
}

async function safeSystemNote(referralId, body) {
  try {
    await createSystemNote(referralId, body);
  } catch (e) {
    console.warn('[referralsApi] system note write failed:', body, e);
  }
}

async function safeUserNote(referralId, body, actor) {
  const trimmed = (body || '').trim();
  if (!trimmed) return;
  try {
    await createNote({
      referralId,
      authorType: 'user',
      authorId: actor?.id || null,
      authorDisplayName: actor?.firstName || 'User',
      body: trimmed.slice(0, NOTE_BODY_MAX),
    });
  } catch (e) {
    console.warn('[referralsApi] user note write failed:', e);
  }
}

/**
 * Apply a whitelisted camelCase patch to a referral row. Only the columns
 * listed here can ever be written; identity fields (prospect_name,
 * referrer_id, channel, received_on, created_by, is_test) are fixed after
 * creation. updated_at is stamped on every write (there is no DB trigger).
 */
async function updateReferralRow(id, patch = {}) {
  const row = { updated_at: new Date().toISOString() };
  if ('pipelineStatus' in patch) row.pipeline_status = patch.pipelineStatus;
  if ('progressContactedAt' in patch) row.progress_contacted_at = patch.progressContactedAt;
  if ('progressMeetingAt' in patch) row.progress_meeting_at = patch.progressMeetingAt;
  if ('nextActionOn' in patch) row.next_action_on = patch.nextActionOn;
  if ('pauseReason' in patch) row.pause_reason = patch.pauseReason;
  if ('outcome' in patch) row.outcome = patch.outcome;
  if ('linkedFileId' in patch) row.linked_file_id = patch.linkedFileId;
  if ('feedbackGivenAt' in patch) row.feedback_given_at = patch.feedbackGivenAt;
  if ('completedAt' in patch) row.completed_at = patch.completedAt;
  if ('reLine' in patch) row.re_line = patch.reLine;
  if ('voiceNoteUrl' in patch) row.voice_note_url = patch.voiceNoteUrl;

  const { data, error } = await supabase
    .from('referrals')
    .update(row)
    .eq('id', id)
    .select('*, referrers(*)')
    .single();

  if (error) throw new Error(`Failed to update referral: ${error.message}`);
  return mapRowToReferral(data);
}

// ── Reads ───────────────────────────────────────────────────────────────────

/**
 * Fetch every referral (newest first) with its referrer embedded. Test
 * records are included — they always render on the board (§33.5); stats
 * utilities filter them out themselves.
 */
export async function getAllReferrals() {
  const { data, error } = await supabase
    .from('referrals')
    .select('*, referrers(*)')
    .order('created_at', { ascending: false });

  if (error) throw new Error(`Failed to load referrals: ${error.message}`);
  return (data || []).map(mapRowToReferral);
}

/**
 * Fetch a single referral by id, with its referrer embedded.
 */
export async function getReferralById(id) {
  const { data, error } = await supabase
    .from('referrals')
    .select('*, referrers(*)')
    .eq('id', id)
    .single();

  if (error) throw new Error(`Failed to load referral: ${error.message}`);
  return mapRowToReferral(data);
}

/**
 * Referrer search for the capture form's "Referred by" autocomplete:
 * case-insensitive substring match on display_name, alphabetical, capped.
 * Returns [] for a blank query. Test referrers are returned with their
 * isTest flag so the UI can badge them.
 */
export async function searchReferrers(query, { limit = 8 } = {}) {
  const q = (query || '').trim();
  if (!q) return [];
  const { data, error } = await supabase
    .from('referrers')
    .select('*')
    .ilike('display_name', `%${escapeLike(q)}%`)
    .order('display_name', { ascending: true })
    .limit(limit);

  if (error) throw new Error(`Failed to search referrers: ${error.message}`);
  return (data || []).map(mapRowToReferrer);
}

/**
 * Fetch every referrer (alphabetical). Small table; used where a full list
 * is simpler than a search.
 */
export async function getAllReferrers() {
  const { data, error } = await supabase
    .from('referrers')
    .select('*')
    .order('display_name', { ascending: true });

  if (error) throw new Error(`Failed to load referrers: ${error.message}`);
  return (data || []).map(mapRowToReferrer);
}

// ── Referrer create-or-reuse ────────────────────────────────────────────────

/**
 * Find a referrer by case-insensitive EXACT display-name match, or create
 * one. When an existing referrer is reused and the caller now says a
 * referral arrangement exists, the flag is promoted to true (it is a fact
 * about the referrer, not the individual referral); it is never demoted
 * here. referrer_type is left as stored on reuse.
 */
export async function findOrCreateReferrer({
  displayName,
  referrerType = 'client',
  hasReferralArrangement = false,
  isTest = false,
}) {
  const name = (displayName || '').trim().replace(/\s+/g, ' ');
  if (!name) throw new Error('Referrer name is required.');
  if (!REFERRER_TYPES.includes(referrerType)) {
    throw new Error(`Unknown referrer type: ${referrerType}`);
  }

  // ilike with the pattern characters escaped is a case-insensitive
  // equality test; lower(display_name) is indexed (migration 28).
  const { data: found, error: findError } = await supabase
    .from('referrers')
    .select('*')
    .ilike('display_name', escapeLike(name))
    .order('created_at', { ascending: true })
    .limit(1);

  if (findError) throw new Error(`Failed to look up referrer: ${findError.message}`);

  if (found && found.length > 0) {
    const existing = found[0];
    if (hasReferralArrangement === true && existing.has_referral_arrangement !== true) {
      const { data: updated, error: updError } = await supabase
        .from('referrers')
        .update({ has_referral_arrangement: true })
        .eq('id', existing.id)
        .select()
        .single();
      if (updError) throw new Error(`Failed to update referrer: ${updError.message}`);
      return mapRowToReferrer(updated);
    }
    return mapRowToReferrer(existing);
  }

  const { data: inserted, error: insError } = await supabase
    .from('referrers')
    .insert({
      display_name: name,
      referrer_type: referrerType,
      has_referral_arrangement: hasReferralArrangement === true,
      is_test: isTest === true,
    })
    .select()
    .single();

  if (insError) throw new Error(`Failed to create referrer: ${insError.message}`);
  return mapRowToReferrer(inserted);
}

// ── Create ──────────────────────────────────────────────────────────────────

/**
 * Create a referral card in Inbox. Creates or reuses the referrer by name.
 *
 * @param {object} data
 * @param {string}  data.referrerName             — "Referred by" (required)
 * @param {string}  [data.referrerType]           — client | professional | other
 * @param {boolean} [data.hasReferralArrangement] — the form's arrangement toggle
 * @param {string}  data.prospectName             — required
 * @param {string}  data.channel                  — phone | whatsapp_sms | email | in_person
 * @param {string}  [data.reLine]                 — the "Re:" line
 * @param {string}  [data.receivedOn]             — 'YYYY-MM-DD'; defaults to today (SAST)
 * @param {string}  [data.nextActionOn]           — 'YYYY-MM-DD' "Follow up by"; defaults
 *                                                  to receivedOn + 2 SA business days
 * @param {string}  [data.voiceNoteUrl]           — signed storage URL of the recording
 * @param {string}  data.createdBy                — the creating user's email (text)
 * @param {boolean} [data.isTest]                 — Adviser-only test flag (§33)
 * @param {object}  [actor]                       — { id, firstName, email, role }
 * @returns {Promise<object>} the created referral (camelCase, referrer embedded)
 */
export async function createReferral(data, actor = null) {
  const prospectName = (data.prospectName || '').trim();
  if (!prospectName) throw new Error('Prospect name is required.');
  if (!REFERRAL_CHANNELS.includes(data.channel)) {
    throw new Error('Received via must be one of: phone, WhatsApp / SMS, email, in person.');
  }

  const receivedOn = data.receivedOn
    ? assertYmd(data.receivedOn, 'Received on')
    : toYmdUTC(addBusinessDays(new Date(), 0));

  let nextActionOn;
  if (data.nextActionOn) {
    nextActionOn = assertYmd(data.nextActionOn, 'Follow up by');
  } else {
    // Reuses the SA business-day helper (same holiday source as the bonus
    // clock and the prep deadline) — never a hand-rolled calendar.
    const due = addBusinessDays(receivedOn, REFERRAL_NEXT_ACTION_BUSINESS_DAYS);
    nextActionOn = toYmdUTC(due);
    if (!nextActionOn) throw new Error('Could not compute the follow-up date.');
  }

  const isTest = data.isTest === true;
  const referrer = await findOrCreateReferrer({
    displayName: data.referrerName,
    referrerType: data.referrerType || 'client',
    hasReferralArrangement: data.hasReferralArrangement === true,
    isTest,
  });

  const row = {
    referrer_id: referrer.id,
    prospect_name: prospectName,
    channel: data.channel,
    re_line: (data.reLine || '').trim() || null,
    received_on: receivedOn,
    pipeline_status: 'inbox',
    next_action_on: nextActionOn,
    voice_note_url: data.voiceNoteUrl || null,
    created_by: data.createdBy || null,
    is_test: isTest,
  };

  const { data: inserted, error } = await supabase
    .from('referrals')
    .insert(row)
    .select('*, referrers(*)')
    .single();

  if (error) throw new Error(`Failed to create referral: ${error.message}`);
  const referral = mapRowToReferral(inserted);

  const who = actor?.firstName ? ` by ${actor.firstName}` : '';
  await safeSystemNote(
    referral.id,
    `Referral received via ${REFERRAL_CHANNEL_LABELS[referral.channel]}${who}. ` +
      `Follow up by ${ymdToDisplay(nextActionOn)}.`
  );
  // One short note only — the summary itself lives on the row (it would not
  // fit the notes' 200-character cap).
  if (referral.voiceNoteUrl) {
    await safeSystemNote(referral.id, 'Voice note recorded.');
  }
  return referral;
}

// ── Status transitions ──────────────────────────────────────────────────────

/**
 * Generic guarded status move. Refuses anything outside
 * REFERRAL_TRANSITIONS. Pause, resume and complete have dedicated helpers
 * below because they write more than the status; this one is for the plain
 * inbox → in_progress "start working" move (and is what the others call
 * for the status write itself).
 */
export async function updateReferralStatus(referral, nextStatus, actor = null) {
  if (!referral || !referral.id) throw new Error('Referral is required.');
  const from = referral.pipelineStatus;
  if (!canTransitionReferral(from, nextStatus)) {
    throw new Error(
      `A referral cannot move from ${REFERRAL_STATUS_LABELS[from] || from} ` +
        `to ${REFERRAL_STATUS_LABELS[nextStatus] || nextStatus}.`
    );
  }
  const updated = await updateReferralRow(referral.id, { pipelineStatus: nextStatus });
  const who = actor?.firstName ? ` by ${actor.firstName}` : '';
  await safeSystemNote(
    referral.id,
    `Referral moved to ${REFERRAL_STATUS_LABELS[nextStatus]}${who}.`
  );
  return updated;
}

/**
 * Stamp (or clear) the two progress ticks: Contacted and Meeting booked.
 * Pass an ISO timestamp (or true for "now") to tick, null to untick, or
 * omit a key to leave it alone. Each change is journaled.
 */
export async function setReferralProgress(referral, { contactedAt, meetingAt } = {}) {
  if (!referral || !referral.id) throw new Error('Referral is required.');
  const patch = {};
  const notes = [];
  if (contactedAt !== undefined) {
    patch.progressContactedAt =
      contactedAt === true ? new Date().toISOString() : contactedAt || null;
    notes.push(patch.progressContactedAt ? 'Prospect contacted.' : 'Contacted tick cleared.');
  }
  if (meetingAt !== undefined) {
    patch.progressMeetingAt = meetingAt === true ? new Date().toISOString() : meetingAt || null;
    notes.push(patch.progressMeetingAt ? 'Meeting booked.' : 'Meeting tick cleared.');
  }
  if (Object.keys(patch).length === 0) return referral;
  const updated = await updateReferralRow(referral.id, patch);
  for (const body of notes) await safeSystemNote(referral.id, body);
  return updated;
}

/**
 * Change the next-action date (editable on the detail view).
 */
export async function setReferralNextAction(referral, nextActionOn) {
  if (!referral || !referral.id) throw new Error('Referral is required.');
  assertYmd(nextActionOn, 'Next action date');
  const updated = await updateReferralRow(referral.id, { nextActionOn });
  await safeSystemNote(referral.id, `Next action moved to ${ymdToDisplay(nextActionOn)}.`);
  return updated;
}

/**
 * In Progress → Paused. Records the reason on the row AND as a human note
 * (the file pause pattern, §18.6), and sets next_action_on to the resume
 * date so the card's footer reads "Resume DD/MM/YYYY".
 */
export async function pauseReferral(referral, reason, resumeOn, actor = null) {
  if (!referral || !referral.id) throw new Error('Referral is required.');
  if (referral.pipelineStatus !== 'in_progress') {
    throw new Error('Only a referral in progress can be paused.');
  }
  const trimmed = (reason || '').trim();
  if (!trimmed) throw new Error('A reason is required to pause a referral.');
  assertYmd(resumeOn, 'Resume date');

  const updated = await updateReferralRow(referral.id, {
    pipelineStatus: 'paused',
    pauseReason: trimmed,
    nextActionOn: resumeOn,
  });
  await safeSystemNote(referral.id, `Referral paused. Resume on ${ymdToDisplay(resumeOn)}.`);
  await safeUserNote(referral.id, trimmed, actor);
  return updated;
}

/**
 * Paused → In Progress. Leaves next_action_on as it is (the resume date
 * becomes the next action) and leaves pause_reason in place as history —
 * the card only shows it while paused, and the next pause overwrites it.
 */
export async function resumeReferral(referral, actor = null) {
  if (!referral || !referral.id) throw new Error('Referral is required.');
  if (referral.pipelineStatus !== 'paused') {
    throw new Error('Only a paused referral can be resumed.');
  }
  const updated = await updateReferralRow(referral.id, { pipelineStatus: 'in_progress' });
  const who = actor?.firstName ? ` by ${actor.firstName}` : '';
  await safeSystemNote(referral.id, `Referral resumed${who}.`);
  return updated;
}

/**
 * In Progress → Completed.
 *
 * @param {object}  referral
 * @param {object}  opts
 * @param {string}  opts.outcome        — converted | declined | no_response
 * @param {boolean} [opts.feedbackGiven] — referrer already thanked / updated
 * @param {string}  [opts.linkedFileId]  — files.id; stored ONLY when converted
 * @param {string}  [opts.closingNote]   — written to the notepad as a human note
 * @param {object}  [actor]
 */
export async function completeReferral(
  referral,
  { outcome, feedbackGiven = false, linkedFileId = null, closingNote = '' } = {},
  actor = null
) {
  if (!referral || !referral.id) throw new Error('Referral is required.');
  if (referral.pipelineStatus !== 'in_progress') {
    throw new Error('Only a referral in progress can be completed.');
  }
  if (!REFERRAL_OUTCOMES.includes(outcome)) {
    throw new Error('Choose an outcome: Converted, Declined or No response.');
  }
  const now = new Date().toISOString();
  const updated = await updateReferralRow(referral.id, {
    pipelineStatus: 'completed',
    outcome,
    completedAt: now,
    feedbackGivenAt: feedbackGiven === true ? now : null,
    linkedFileId: outcome === 'converted' ? linkedFileId || null : null,
  });
  const who = actor?.firstName ? ` by ${actor.firstName}` : '';
  await safeSystemNote(
    referral.id,
    `Referral completed${who}: ${REFERRAL_OUTCOME_LABELS[outcome]}.` +
      (feedbackGiven === true ? ' Feedback given to referrer.' : '')
  );
  await safeUserNote(referral.id, closingNote, actor);
  return updated;
}

/**
 * Record that the referrer has been thanked / updated on the outcome.
 * Idempotent: an already-stamped referral is returned unchanged.
 */
export async function markReferralFeedbackGiven(referral, actor = null) {
  if (!referral || !referral.id) throw new Error('Referral is required.');
  if (referral.feedbackGivenAt) return referral;
  const updated = await updateReferralRow(referral.id, {
    feedbackGivenAt: new Date().toISOString(),
  });
  const who = actor?.firstName ? ` by ${actor.firstName}` : '';
  await safeSystemNote(referral.id, `Feedback given to referrer${who}.`);
  return updated;
}
